/**
 * What the composer is composing.
 *
 * `InvoiceComposerPage` is one screen with a live tax preview in it, and the
 * proforma composer has to agree with it exactly - the whole point of a
 * proforma is that it shows the figure the customer will eventually be billed.
 * Two copies of that screen would agree on the day they were written and drift
 * afterwards, so instead there is one screen and this table of the handful of
 * things that actually differ.
 *
 * Everything not in here - the line editor, the tax preview, the item picker,
 * what gets sent to the server - is identical by construction rather than by
 * anybody keeping two files in step.
 */
export interface DocumentKindConfig {
  /** The API collection, used for the route, the POST and the next-number peek. */
  path: 'invoices' | 'proformas';
  title: string;
  /** What the primary date field is called on this document. */
  dateLabel: string;
  /**
   * The second date, and what it means. An invoice has a DUE date - money is
   * owed by then. A proforma has a VALID UNTIL date - nothing is owed, so
   * nothing can be overdue; the offer simply lapses.
   */
  secondDate: { field: 'dueDate' | 'expiryDate'; label: string; hint?: string };
  issueLabel: string;
  /**
   * A draft of this kind is numbered out of its OWN series (DRAFT-00007) and
   * takes its real number when it is issued - migration 0025, invoices only so
   * far. It changes what the header can promise: "this will be INV-00042" is a
   * lie about a document that may never be issued, and the number the next
   * issued invoice gets is not the number this draft will get if three other
   * drafts are issued first.
   */
  draftsAreNumberedSeparately?: boolean;
  /** Query keys to drop after a save, because this document moved those figures. */
  invalidates: string[];
}

export const DOCUMENT_KINDS = {
  invoice: {
    path: 'invoices',
    title: 'New invoice',
    dateLabel: 'Invoice date',
    secondDate: { field: 'dueDate', label: 'Due date', hint: 'Left blank, this follows the payment term.' },
    issueLabel: 'Save and issue',
    draftsAreNumberedSeparately: true,
    // An invoice is a receivable the moment it is issued, so the dashboard
    // tile it moves has to be refetched.
    invalidates: ['invoices', 'dashboard'],
  },
  proforma: {
    path: 'proformas',
    title: 'New proforma invoice',
    dateLabel: 'Proforma date',
    secondDate: {
      field: 'expiryDate',
      label: 'Valid until',
      hint: 'After this the offer lapses. Nothing is owed, so nothing goes overdue.',
    },
    issueLabel: 'Save and send',
    // A proforma draft still takes the next PI- number when it is saved. A
    // quote's number is quoted at the customer while it is still a draft, so
    // there is nothing to gain by holding it back - and a gap in the PI-
    // series is nobody's audit question.
    draftsAreNumberedSeparately: false,
    // Deliberately NOT 'dashboard'. A proforma moves no receivable, and
    // invalidating that tile would imply to a reader that it does.
    invalidates: ['proformas'],
  },
} as const satisfies Record<string, DocumentKindConfig>;

export type DocumentKind = keyof typeof DOCUMENT_KINDS;
